import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppSelector } from '../../../../src/store/storeTypes';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import useWindowDimensions from '../../../hooks/useWindowDimensions';
import CustomiseHeader from '../components/CustomiseHeader';
import CustomiseSection from '../components/CustomiseSection';
import {
  calcSubTotal,
  addParentReferences,
  dereffItems,
  applyDefaultSubProducts,
  useQuickAddProperties
} from '../../../helpers/basketHelpers';
import { customItemOptionsToGtmParams } from '../../../helpers/gtmEventHelpers';
import hash from 'object-hash';
import { useGetSingleMenuItemQuery } from '../../../services/restaurant.api';
import { historyMW } from '../../../helpers/routingHelpers';
import PopupBannerController from '../../../components/PopupBannerController';
import { useAddToBasketMutation, useUpdateBasketMutation } from '../../../services/basket.api';
import { freshRelevance, gtmEvent } from '../../../helpers/commonHelpers';
import { useConfig } from '../../../helpers/useConfig';
import chevronLeft from '../../../img/icons/ChevronLeft24.svg';
import minimize from '../../../img/icons/minus-outlined-white.svg';
import plus from '../../../img/icons/plus-outlined-white.svg';
import { Tags } from '../../../components/Tags';
import Loading from '../../../components/Loading';
import { ChoiceModalWrapper, PizzaDescriptionModalWrapper } from '../../../helpers/modalHelpers';
import { useCustomiseScrollHelpers } from '../../../hooks/useCustomiseScrollHelper'
import { getCustomItem } from '../../../helpers/customiseHelpers';

const dietIcon = ['hot', 'gluten free', 'vegan', 'vegetarian'];

export const Customise = ({ refs, restaurantId, refreshBasket, setNotification }) => {
  const config = useConfig();
  const basketItems = useSelector(state => state.basket.items);
  const basket = useSelector(state => state.basket);
  const baseSelections = useSelector(state => state.menu.baseSelections);
  const outOfStockItems = useSelector(state => state.menu.outOfStockItems);
  const isDelivery = useSelector(state => state.basket.isDelivery);
  const inApp = useSelector(state => state.session.inApp);
  const showCalories = useAppSelector(state => state.menu.showCalories);
  const customer = useSelector(state => state.customer);
  const { itemId } = useParams();
  const { width: screenWidth } = useWindowDimensions();
  const navigate = useNavigate();
  const [showErrors, setShowErrors] = useState(false);
  const [currentPrice, setCurrentPrice] = useState(0);
  const { items, bundles, numberOfItems } = useSelector(state => state.basket);
  const [addToBasket] = useAddToBasketMutation();
  const [updateBasket] = useUpdateBasketMutation();
  const duoRomanaIds = useSelector(state => state.session.frontEndConfig?.duoRomanaIds);
  const pageRef = useRef(null);
  const currentTier = useSelector(state => state.session?.customer?.loyalty?.customer?.currentTier?.name);
  const loyaltySchemeVersion = useAppSelector(state => state.session?.customer?.loyalty?.customer?.loyaltySchemeVersion);
  const isTabletScreen = useSelector(state => state.session.isTabletScreen);
  const customiseBannerRef = useRef(null);
  const { state } = useLocation();
  const [params] = useSearchParams();
  const { customId } = Object.fromEntries(params);
  const { data: menuItem, isLoading, isFetching } = useGetSingleMenuItemQuery({ id: restaurantId, itemId }, { skip: !restaurantId || state.menuItem });
  const [item, setItem] = useState(addParentReferences(state?.menuItem));
  const isDuoRomana = duoRomanaIds?.includes(item?.id);
  const [customisedObj, setCustomisedObj] = useState(getCustomItem(item, customId, basketItems, baseSelections, isDuoRomana));
  const [selectedOptions, setSelectedOptions] = useState({});
  const [invalidSections, setInvalidSections] = useState([]);
  const [quantity, setQuantity] = useState(customisedObj?.quantity <= 0 ? 1 : customisedObj?.quantity);
  const { isCustomisable, isPizza: itemIsPizza, shouldHoistTags } = useQuickAddProperties(item);
  const isPizza = itemIsPizza || isDuoRomana;
  const [customiseContainerMinHeight, setCustomiseContainerMinHeight] = useState();
  const [containerMinHeight, setContainerMinHeight] = useState();
  const [navHeight, setNavHeight] = useState(refs.current.navRef?.offsetHeight ?? 0);
  const { width } = useWindowDimensions();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalOption, setModalOption] = useState([]);
  const [choiceModalState, setChoiceModalState] = useState(false);
  const customisePageRef = useRef();
  const { scrollToRequired, scrollToInvalidSection } = useCustomiseScrollHelpers(refs, customiseBannerRef, customisePageRef, invalidSections);

  useEffect(() => {
    if ((!isLoading || menuItem) && !state?.menuItem) {
      if (menuItem) {
        const itemWithParents = addParentReferences(menuItem);
        setItem(itemWithParents);
      } else if (restaurantId) {
        historyMW.push(`/menu/${restaurantId}`, isDelivery, navigate);
      }
    }
  }, [menuItem, isLoading, customId, state?.menuItem]);

  const addItemToBasket = async () => {
    const sections = Array.from(document.getElementsByClassName('customise-section'));
    const firstInvalidSectionNode = sections.filter(el => invalidSections.find(s => el.dataset.sectionErrorId === s))[0];
    if (invalidSections.length && firstInvalidSectionNode) {
      scrollToInvalidSection(firstInvalidSectionNode);
      setShowErrors(true);
    } else if (customId) {
      await updateBasket({
        restaurantId,
        numberOfItems,
        items: items.map(item => item.customID === customId ? { ...customisedObj, quantity, customID: hash(customisedObj) } : item),
        bundles
      });
      refreshBasket();
      setNotification('Item added to order');
      historyMW.push('/basket', isDelivery, navigate);
    } else {
      await addToBasket([{ ...customisedObj, calculatedCost: currentPrice * quantity, quantity, customID: hash(customisedObj), restaurantId }]);
      freshRelevance('pageChange', null, { 'sendBeacon': true });
      refreshBasket();

      const event = {
        channel: inApp ? 'App' : 'Web',
        currency: config?.BRAND === 'PE' ? 'GBP' : 'EUR',
        value: (customisedObj.calcSubTotal() * quantity).toFixed(2),
        clickAndCollectEvent: isDelivery
          ? 'Delivery_Product_Details_Clicked'
          : 'Collection_Product_Details_Clicked',
      };

      const item = {
        item_id: customisedObj.id,
        item_name: customisedObj.name,
        price: customisedObj.calcSubTotal(),
        quantity: quantity,
        item_brand: config?.BRAND,
        item_category: customisedObj.categoryName
      };

      if (customer?.customerSessionToken?.pizzaExpressId) {
        event.customerId = customer.customerSessionToken.pizzaExpressId;
      }

      customItemOptionsToGtmParams(customisedObj, item);

      event['items'] = [item];
      gtmEvent('add_to_cart', event);

      setNotification('Item added to order');
      navigate(-1);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      window.scrollTo({ left: 0, top: 0 });
    }, 150);

    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    if (customId && item && !customisedObj) {
      historyMW.push(`/menu/${restaurantId}`, isDelivery, navigate);
    }
    else if (item && item?.id !== customisedObj?.id) {
      resetItem();
    }
  }, [item, customisedObj, params]);

  const resetItem = () => {
    const baseSelectionOverride = baseSelections.find(b => b.product === item.id);
    const largeClassicBase = item.subProducts[0].defaultSubProduct === '111';

    if (baseSelectionOverride) {
      item.subProducts[0].defaultSubProduct = baseSelectionOverride.base;
    }

    const defaultItem = applyDefaultSubProducts(item, isDuoRomana);
    const dereffedItem = dereffItems(defaultItem);

    setCustomisedObj({ ...dereffedItem, customID: customId });

    // Reset all selected options and only keep the base if it was selected from the base dropdown
    const question = item.subProducts[0];
    const selectedOptionItemId = `${question?.name.toLowerCase().trim()}.${question?.name}`;
    setSelectedOptions(baseSelectionOverride || largeClassicBase ? { [selectedOptionItemId]: selectedOptions[selectedOptionItemId] } : {});
    setShowErrors(false);
  };

  const handleSelectedOptionsChange = (sectionId, options) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [sectionId]: options,
    }));
  };

  const clearOptionsForSection = (sectionId) => {
    setSelectedOptions((prev) => ({
      ...prev,
      [sectionId]: [],
    }));
  }

  useEffect(() => {
    if (customisedObj) {
      customisedObj.calcSubTotal && setCurrentPrice(customisedObj.calcSubTotal());
    }
  }, [customisedObj]);

  const handleBaseNameClick = (e, option) => {
    if (!isDuoRomana || !option?.imageUrl) return;

    e.stopPropagation();
    setModalOption(option);
    setModalIsOpen(true);
  }

    const setContainerHeight = () => {
        const zoomLevel = window.outerWidth / window.innerWidth;
        if (zoomLevel < 1) {
            setContainerMinHeight('90vh')
        }
        else {
            setContainerMinHeight('0')
        }
    };

    window.addEventListener('resize', () => {
        setContainerHeight();
    });

    useLayoutEffect(() => {
        setNavHeight(refs.current.navRef?.offsetHeight ?? 0);
    }, [refs])

    useLayoutEffect(() => {
        setContainerHeight();
        setCustomiseContainerMinHeight(`${(customisePageRef.current?.clientHeight ?? 0) - (refs.current.footer?.clientHeight ?? 0) - (refs.current.navRef?.clientHeight ?? 0) + customiseBannerRef?.current?.clientHeight ?? 0}px`);
    }, [navHeight])

    if (isLoading || isFetching || !item || !customisedObj) {
    return (
      <div
        style={{
          width: '100vw',
          position: 'fixed',
          top: navHeight ?? 0,
          left: 0,
          bottom: 0,
          zIndex: 10000,
          backgroundColor: '#eae7e4',
          overflow: 'auto'
        }}
      >
        <Loading />
      </div>
    );
  }

  return (
    <main
      id="mainContainer"
      ref={customisePageRef}
    >
      <div className='customise-banner' style={{ top: navHeight ?? 0 }} ref={customiseBannerRef}>
        {!inApp ? (
          <button className={'btn-customise-back'} onClick={() => navigate(-1)}>
            <img src={chevronLeft} alt="Chevron" />
            Back to {customId ? 'basket' : 'menu'}
          </button>
        ) : (
          <div></div>
        )}
      </div>
      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start' }}>
        <div className='customise-container'
          ref={pageRef}
                  style={{ minHeight: containerMinHeight && containerMinHeight !== '0' ? `${containerMinHeight}` : `${customiseContainerMinHeight}` }}>
          {item &&
            <div className='customise-product-info-container' style={isTabletScreen ? {} : { top: (customiseBannerRef?.current?.clientHeight ?? 0), position: 'sticky' }}>
              <img className='customise-product-image' src={item?.imageUrl} alt='food item' />
              <div className='customise-product-info'>
                <Tags
                  item={item}
                  currentTier={currentTier}
                  loyaltySchemeVersion={loyaltySchemeVersion}
                  showLocked={false}
                  excludeChildTags={isCustomisable && !shouldHoistTags}
                  wrapperClass={'all-tags-wrapper '}
                />
                <CustomiseHeader
                  dietIcon={dietIcon}
                  name={item?.name}
                  description={item?.description}
                  restaurantId={restaurantId}
                  screenWidth={screenWidth}
                  quantity={quantity}
                  setQuantity={setQuantity}
                  customisedItem={customisedObj}
                  setShowErrors={setShowErrors}
                  invalidSections={invalidSections}
                  edit={!!customId}
                  refreshBasket={refreshBasket}
                  currentPrice={currentPrice}
                  addToBasket={addItemToBasket}
                  inApp={inApp}
                  item={item}
                  showCalories={showCalories}
                />
              </div>
            </div>
          }
          <div className='customise-card'>
            {item && !item.outOfStock &&
              item?.subProducts?.map((el, idx) => (
                <CustomiseSection
                  key={`${el.id}.${idx}`}
                  item={{ ...el, calcSubTotal }}
                  NewComponent={CustomiseSection}
                  result={customisedObj}
                  setCustomisedObj={setCustomisedObj}
                  invalidSections={invalidSections}
                  setInvalidSections={setInvalidSections}
                  showErrors={showErrors}
                  resetItem={resetItem}
                  outOfStockItems={outOfStockItems}
                  isDuoRomana={isDuoRomana}
                  duoRomanaIds={duoRomanaIds}
                  handleRequiredScrolling={scrollToRequired}
                  currentTier={currentTier}
                  loyaltySchemeVersion={loyaltySchemeVersion}
                  showCalories={showCalories}
                  customId={customId}
                  selectedOptions={selectedOptions}
                  onSelectedOptionsChange={(options, id) => handleSelectedOptionsChange(id, options)}
                  isPizza={isPizza}
                  quantity={quantity}
                  clearOptionsForSection={clearOptionsForSection}
                  setNotification={setNotification}
                  handleBaseNameClick={handleBaseNameClick}
                  setChoiceModalState={setChoiceModalState}
                />
              ))
            }
          </div>
        </div>
        <div
          ref={r => refs.current.footerSpacer = r}
          style={{
            width: '100%',
            height: inApp ? refs.current.bannerRef?.offsetHeight : 0
          }} />
        <PopupBannerController refs={refs} customise justifyEnd>
          <div className='customise-popup-container'>
            <div className='customise-card-amount-container'>
              {
                <>
                  <img
                    alt="minus"
                    src={minimize}
                    className="icon is-medium"
                    aria-label='minus'
                    style={{ cursor: quantity > 1 && !item.outOfStock ? 'pointer' : '', opacity: quantity > 1 && !item?.outOfStock ? '1' : '0.2' }}
                    aria-disabled={item?.outOfStock}
                    onClick={() => !item?.outOfStock && setQuantity(prev => prev > 1 ? prev - 1 : prev)}
                  />
                  <p className="customise-card-amount-quantity" style={{ color: 'white' }}>{quantity}</p>
                  <img
                    alt="plus"
                    src={plus}
                    className="icon is-medium"
                    aria-label='plus'
                    style={{ cursor: !item?.outOfStock ? 'pointer' : '', opacity: item?.outOfStock ? '0.2' : '1' }}
                    aria-disabled={item?.outOfStock}
                    onClick={() => !item?.outOfStock && setQuantity(prev => prev + 1)}
                  />
                </>
              }
            </div>
            <button className={`checkout-btn btn btn-primary`} disabled={item?.outOfStock} style={{ width: 'min-content' }} onClick={addItemToBasket}>
              Add to basket - {config?.BRAND === 'PE' ? '£' : '€'}{(currentPrice * quantity).toFixed(2)}
            </button>
          </div>
        </PopupBannerController>
      </div>
      {isDuoRomana && <PizzaDescriptionModalWrapper
        inApp={inApp}
        isOpen={modalIsOpen}
        setIsOpen={setModalIsOpen}
        screenWidth={width}
        item={modalOption}
      />}
      {
        !!choiceModalState &&
        <ChoiceModalWrapper
          isOpen={!!choiceModalState}
          state={choiceModalState}
          onClose={() => setChoiceModalState(false)}
          confirmButtonText={'Save selection'}
          screenWidth={width} />
      }
    </main>

  );
};

export default Customise;